/**
 * Patient Profile Mapper
 *
 * Maps patient data from existing hooks into the PredictionInput
 * expected by the IVF prediction engine.
 *
 * See docs/features/IVF_PREDICTION_MODEL.md for mapping rationale.
 */

import type { Patient } from "@louise/patients/patients.types";
import type { FertilityIntent } from "@louise/fertility-intent/fertilityIntent.types";
import type { PredictionInput, DiagnosisCategory } from "./ivfPredictionEngine";

type MapperOptions = {
  /** Explicitly set if patient is using donor eggs (not inferrable from API) */
  isUsingDonorEggs?: boolean;
};

// ---------------------------------------------------------------------------
// Disease name → DiagnosisCategory mapping
// ---------------------------------------------------------------------------

const DISEASE_TO_DIAGNOSIS: Record<string, DiagnosisCategory> = {
  pcos: "pcos",
  endometriosis: "endometriosis",
  diminished_ovarian_reserve: "diminished_ovarian_reserve",
  primary_ovarian_insufficiency: "diminished_ovarian_reserve",
  poor_ovarian_reserve: "diminished_ovarian_reserve",
};

/**
 * Maps fertility intent reported conditions to diagnosis categories.
 * These are patient-reported (not clinically confirmed) so they serve
 * as a fallback when diseases[] is empty.
 */
const REPORTED_CONDITION_TO_DIAGNOSIS: Record<string, DiagnosisCategory> = {
  pcos: "pcos",
  endometriosis: "endometriosis",
  low_ovarian_reserve: "diminished_ovarian_reserve",
  early_menopause: "diminished_ovarian_reserve",
  fibroids: "uterine_factor",
  tubal_issues: "tubal_factor",
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function calculateAge(dateOfBirth: string): number {
  const dob = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

function getDiagnosesFromDiseases(
  diseases: { disease?: string | null; cured?: boolean | null }[],
): DiagnosisCategory[] {
  const fertilityDiagnoses: DiagnosisCategory[] = [];

  for (const d of diseases) {
    if (!d.disease || d.cured) continue;
    const mapped = DISEASE_TO_DIAGNOSIS[d.disease];
    if (mapped && !fertilityDiagnoses.includes(mapped)) {
      fertilityDiagnoses.push(mapped);
    }
  }

  return fertilityDiagnoses;
}

function getDiagnosesFromDiseaseNames(
  conditions: readonly string[],
): DiagnosisCategory[] {
  const fertilityDiagnoses: DiagnosisCategory[] = [];

  for (const c of conditions) {
    const mapped = REPORTED_CONDITION_TO_DIAGNOSIS[c];
    if (mapped && !fertilityDiagnoses.includes(mapped)) {
      fertilityDiagnoses.push(mapped);
    }
  }

  return fertilityDiagnoses;
}

function getSmokingStatus(
  addictions: {
    substance?: string | null;
    frequency?: string | null;
  }[],
): { isSmoking: boolean; isFormerSmoker: boolean } {
  for (const a of addictions) {
    if (a.substance !== "cigarettes" && a.substance !== "vape") continue;

    if (a.frequency === "stopped") {
      return { isSmoking: false, isFormerSmoker: true };
    }
    if (
      a.frequency === "daily" ||
      a.frequency === "weekly_or_less" ||
      a.frequency === "monthly_or_less"
    ) {
      return { isSmoking: true, isFormerSmoker: false };
    }
  }
  return { isSmoking: false, isFormerSmoker: false };
}

// ---------------------------------------------------------------------------
// Cycle types
// ---------------------------------------------------------------------------

type CycleData = {
  cycleType?: string | null;
  status?: string | null;
};

function countFailedIvfCycles(cycles: CycleData[]): number {
  return cycles.filter(
    (c) =>
      (c.cycleType === "ivf" || c.cycleType === "icsi") &&
      c.status === "closed",
  ).length;
}

// ---------------------------------------------------------------------------
// Main mapper
// ---------------------------------------------------------------------------

export function mapPatientProfile(
  patient: Patient,
  fertilityIntent: FertilityIntent | null,
  cycles: CycleData[],
  options?: MapperOptions,
): PredictionInput | null {
  // Age is required
  if (!patient.dateOfBirth) return null;
  const age = calculateAge(patient.dateOfBirth);

  // Diagnoses: prefer clinically-entered diseases, fall back to reported conditions
  const diseases = patient.medicalHistory?.diseases ?? [];
  const diagnoses = getDiagnosesFromDiseases(diseases);

  // Check for male factor via varicoceles or medical history flag
  if (
    !diagnoses.includes("male_factor") &&
    patient.medicalHistory?.hadVaricoceles === "yes_diagnosed"
  ) {
    diagnoses.push("male_factor");
  }

  // Check for endometriosis via medical history flag
  if (
    !diagnoses.includes("endometriosis") &&
    (patient.medicalHistory?.hasEndometriosis === "yes_diagnosed" ||
      patient.medicalHistory?.hasEndometriosis === "yes_undiagnosed")
  ) {
    diagnoses.push("endometriosis");
  }

  // Check for PCOS via medical history flag
  if (!diagnoses.includes("pcos") && patient.medicalHistory?.suspectPCOS) {
    diagnoses.push("pcos");
  }

  // Fall back to diseases for diagnosis inference (exclude cured, matching
  // the primary path) using the legacy reported-condition vocabulary.
  if (diagnoses.length === 0) {
    diagnoses.push(
      ...getDiagnosesFromDiseaseNames(
        diseases
          .filter((d) => !d.cured)
          .map((d) => d.disease ?? "")
          .filter((d) => !!d),
      ),
    );
  }

  // Donor eggs — must be explicitly set via options (not inferrable from API)
  const isUsingDonorEggs = options?.isUsingDonorEggs ?? false;

  // BMI
  const bmi = patient.bodyData?.bmi ?? null;

  // Smoking
  const addictions = patient.lifestyle?.addictions ?? [];
  const { isSmoking, isFormerSmoker } = getSmokingStatus(addictions);

  // Prior pregnancies
  const pregnancies = patient.obGynHistory?.pregnancies ?? [];
  const hasPriorLiveBirth = pregnancies.some((p) => p.outcome === "live_birth");
  const hasPriorMiscarriage =
    !hasPriorLiveBirth && pregnancies.some((p) => p.outcome === "miscarriage");

  // Prior failed IVF cycles
  const priorFailedIvfCycles = countFailedIvfCycles(cycles);

  return {
    age,
    diagnoses,
    priorFailedIvfCycles,
    isUsingDonorEggs,
    bmi,
    isSmoking,
    isFormerSmoker,
    hasPriorLiveBirth,
    hasPriorMiscarriage,
  };
}
